import { useCallback, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useObjectMemo } from "../../../hooks";

import { OnboardingPromptStatus } from "./types";
import { actions } from "./slice";
import { useFetchOnboardingPrompts } from "./requests";
import { selectOnboardingData } from "./selectors";

import type {
  OnboardingPrompt,
  OnboardingPlatform,
  OnboardingPromptResponse,
} from "./types";
import type { RootState } from "../../entities/store";

export const usePercentComplete = (prompts: OnboardingPrompt[]) => {
  const uncompletedPromptsCount = useMemo(
    () =>
      Array.isArray(prompts)
        ? prompts.filter(
            (prompt) => prompt.status === OnboardingPromptStatus.inProgress,
          ).length
        : 0,
    [prompts],
  );

  const percentComplete = useMemo(
    () =>
      Array.isArray(prompts) && prompts.length > 0
        ? Math.round(
            100 * ((prompts.length - uncompletedPromptsCount) / prompts.length),
          )
        : 0,
    [prompts, uncompletedPromptsCount],
  );

  return useObjectMemo({
    uncompletedPromptsCount,
    percentComplete,
  });
};

export const useOnboardingContainer = (platform: OnboardingPlatform) => {
  const { fetchOnboardingPrompts: fetchOnboardingPromptsRequest, loading } =
    useFetchOnboardingPrompts();
  const dispatch = useDispatch();
  const { id, title, description, prompts } = useSelector((state: RootState) =>
    selectOnboardingData(state),
  );

  const fetchOnboardingPrompts = useCallback(() => {
    fetchOnboardingPromptsRequest(platform, {
      onData: (apiResponse: OnboardingPromptResponse) =>
        dispatch(actions.upsert({ ...apiResponse, platform })),
      onError: () => dispatch(actions.clearAll()),
    });
  }, [fetchOnboardingPromptsRequest, dispatch, platform]);

  const { uncompletedPromptsCount, percentComplete } =
    usePercentComplete(prompts);

  return useObjectMemo({
    id,
    title,
    description,
    prompts,
    fetchOnboardingPrompts,
    loading,
    uncompletedPromptsCount,
    percentComplete,
  });
};
